import React, { useState, useRef, Fragment } from 'react'

import CompletedCard from './version_2/CompletedCard'
import { ModalAlert } from '@reactiveonline/reactive_admin_shared-packages'

const contractSteps = [
  { key: 0, icon: 'fa-envelope-open-text' },
  { key: 10, icon: 'fa-check-to-slot' },
  { key: 20, icon: 'fa-file-signature' },
  { key: 30, icon: 'fa-file-check' },
  { key: 40, icon: 'fa-envelope-circle-check' },
  { key: 50, icon: 'fa-circle-check' }
]

export default function Version2({
  contractData, invoicingOnboarding, apiIntegrationSettings, stores,
  canManageInvoicingOnboarding, notifyCustomerForApiKeysPath,
  saveApiIntegrationKeysPath, invoicingStatusUpdatePath, loading, setLoading,
  flashMessageRef, taxDocuments, skipInvoicingOnboardingPath, canSkipInvoicingOnboarding, cancelOnboardingPath, setInvoicingSoftwareData
}) {
  const [notifiedForStatusCode, setNotifiedForStatusCode] = useState(initializeNotifiedForStatusCode)
  const [showNotifyCustomerAlert, setShowNotifyCustomerAlert] = useState(false)
  const [showCancelAlert, setShowCancelAlert] = useState(false)
  const [showSkipOnboardingProcessAlert, setShowSkipOnboardingProcessAlert] = useState(false)

  const { number, contractStatusCode, hasSignedContract } = contractData || {}
  const currentStep = invoicingOnboarding?.currentStep
  const onboardingStarted = !!currentStep && currentStep !== 'cancelled'
  const onboardingCompleted = currentStep === 'completed'
  const onboardingNotStarted = !onboardingStarted
  const canNotifyForStatusUpdate = currentStep !== 'completed' && notifiedForStatusCode < contractStatusCode

  function initializeNotifiedForStatusCode() {
    const notifiedForStatusSetting = invoicingOnboarding?.settings?.find( setting => setting.key === 'notified_for_status' )
    return parseInt(notifiedForStatusSetting?.value || 0)
  }

  function handleNotifyCustomer() {
    setLoading(true)
    setShowNotifyCustomerAlert(false)

    let formData = new FormData()
    formData.append('contract_status_code', contractStatusCode)

    Rails.ajax({
      type: 'POST',
      url: invoicingStatusUpdatePath,
      data: formData,
      dataType: 'json',
      success: result => {
        setNotifiedForStatusCode(contractStatusCode)
        flashMessageRef.current.show(result.message, 'success')
      },
      error: result => {
        flashMessageRef.current.show(result.error, 'error')
      },
      complete: result => {
        setLoading(false)
      }
    })
  }

  function handleSkipOnboardingProcess(){
    setLoading(true)
    setShowSkipOnboardingProcessAlert(false)

    Rails.ajax({
      type: 'POST',
      url: skipInvoicingOnboardingPath,
      success: res => {
        flashMessageRef.current.show(res.message, 'success')
        window.location.reload()
      }
    })
  }

  function handleCancelOnboarding(){
    setLoading(true)
    setShowCancelAlert(false)

    Rails.ajax({
      type: 'POST',
      url: cancelOnboardingPath,
      success: res => {
        setInvoicingSoftwareData(prevState => ({
          ...prevState,
          contractData: null,
          invoicingOnboarding: {
            ...(prevState?.invoicingOnboarding || {}),
            currentStep: 'cancelled',
            settings: prevState?.invoicingOnboarding?.settings || []
          }
        }))
        flashMessageRef.current.show(res.message, 'success')
        setLoading(false)
      },
      error: res => {
        flashMessageRef.current.show(res.error, 'error')
        setLoading(false)
      }
    })
  }

  return (
    <>
      { canManageInvoicingOnboarding &&
        <div className='card'>
          <div className='card-title flex-box content-space-between flex-wrap' style={{ gap: 20 }}>
            <h2>{ ReactiveCore.translations.invoicing_onboarding.invoicing_onboarding_header }</h2>
            { onboardingNotStarted && canSkipInvoicingOnboarding ?
              <button
                onClick={ () => setShowSkipOnboardingProcessAlert(true) }
                className='btn'
              >
                { ReactiveCore.translations.invoicing_onboarding.complete_process }
              </button>
              : onboardingStarted &&
              <button
                onClick={ () => setShowCancelAlert(true) }
                className='btn reverted'
              >
                { ReactiveCore.translations.invoicing_onboarding.cancel_process }
              </button>
            }
          </div>
        </div>
      }

      { onboardingStarted && !onboardingCompleted &&
        <div className='card onboarding-wrapper'>
          { canNotifyForStatusUpdate &&
            <div className='flex-box content-space-between items-center mb20'>
              <div className='onboarding-warning flex-box items-center'>
                <i className='fal fa-exclamation-circle mr5'/>

                <div className='onboarding-warning-text'>
                  { ReactiveCore.translations.invoicing_onboarding.pending_customer_notification }
                </div>
              </div>

              <div
                className='onboarding-button btn flex-box items-center'
                onClick={ () => setShowNotifyCustomerAlert(true) }
              >
                <i className='fal fa-envelope mr5'/>

                <div>
                  { ReactiveCore.translations.invoicing_onboarding.notify_customer }
                </div>
              </div>
            </div>
          }

          <div className='onboarding-title'>
            { ReactiveCore.translations.invoicing_onboarding.contract_number }
          </div>

          <div className='onboarding-subtitle mt5 mb10'>
            { number }
          </div>

          <div className='onboarding-title'>
            { ReactiveCore.translations.invoicing_onboarding.contract_status }
          </div>

          { contractSteps.map( (contractStep, index) => {
            const passedStep = contractStatusCode !== null && contractStatusCode >= contractStep.key
            const isNextStep = (contractStatusCode + 10) === contractStep.key
            let stepSubtitle = ReactiveCore.translations.invoicing_onboarding[`contract_status_${ contractStep.key }_subtitle`]

            if (contractStep.key === 20 && hasSignedContract) {
              stepSubtitle = ReactiveCore.translations.invoicing_onboarding[`contract_status_${ contractStep.key }_subtitle_2`]
            }

            return (
              <Fragment key={ contractStep.key }>
                <div className={ `onboarding-step-wrapper ${ isNextStep ? 'active' : '' }` }>
                  <div className='flex-box items-center content-space-between'>
                    <div className={ `onboarding-step-title ${ passedStep ? 'success' : (isNextStep ? 'active' : '') } flex-box items-center` }>
                      <i className={ `fal ${ contractStep.icon }` }/>

                      <div className='onboarding-step-title-text'>
                        { ReactiveCore.translations.invoicing_onboarding[`contract_status_${ contractStep.key }`] }
                      </div>
                    </div>

                    { passedStep &&
                      <i className='fal fa-check onboarding-step-success-icon'/>
                    }
                  </div>

                  { isNextStep &&
                    <div className='onboarding-step-subtitle'>
                      { stepSubtitle }
                    </div>
                  }
                </div>

                { index < (contractSteps.length - 1) &&
                  <div className='divider light mv5'/>
                }
              </Fragment>
            )
          })}
        </div>
      }

      { onboardingCompleted &&
        <CompletedCard
          canManageInvoicingOnboarding={ canManageInvoicingOnboarding }
          apiIntegrationSettings={ apiIntegrationSettings }
          stores={ stores }
          notifyCustomerForApiKeysPath={ notifyCustomerForApiKeysPath }
          saveApiIntegrationKeysPath={ saveApiIntegrationKeysPath }
          invoicingOnboarding={ invoicingOnboarding }
          loading={ loading }
          setLoading={ setLoading }
          flashMessageRef={ flashMessageRef }
          taxDocuments={ taxDocuments }
        />
      }

      { onboardingNotStarted &&
        <div className='card'>
          <div className='card-content'>
            <div className="card-body">
              <div className='flex-box content-center items-center'>
                { ReactiveCore.translations.invoicing_onboarding.process_not_started }
              </div>
            </div>
          </div>
        </div>
      }

      { showNotifyCustomerAlert &&
        <ModalAlert
          onClose={ () => setShowNotifyCustomerAlert(false) }
          alert={ ReactiveCore.translations.invoicing_onboarding.status_update_alert }
          acceptText={ ReactiveCore.translations.invoicing_onboarding.notify_customer }
          onSelect={ handleNotifyCustomer }
        />
      }

      { showCancelAlert &&
        <ModalAlert
          cancelText={ ReactiveCore.translations.invoicing_onboarding.cancel }
          acceptText={ ReactiveCore.translations.invoicing_onboarding.accept }
          alert={ ReactiveCore.translations.invoicing_onboarding.cancellation_alert }
          onSelect={ handleCancelOnboarding }
          onClose={ () => setShowCancelAlert(false) }
        />
      }

      { showSkipOnboardingProcessAlert &&
        <ModalAlert
          cancelText={ ReactiveCore.translations.invoicing_onboarding.cancel }
          acceptText={ ReactiveCore.translations.invoicing_onboarding.accept }
          alert={ ReactiveCore.translations.invoicing_onboarding.skip_onboarding_process_alert }
          onSelect={ handleSkipOnboardingProcess }
          onClose={ () => setShowSkipOnboardingProcessAlert(false) }
        />
      }
    </>
  )
}
